import React, {useCallback, useState} from 'react';
import {StyleSheet} from 'react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import type {PersonnelStackParamList} from '@/app/routes';
import {PeopleList} from '@/features/view-people/ui/PeopleList';
import {PersonnelCard} from '@/features/manage-personnel/ui/PersonnelCard';
import {PersonnelToolsSheet} from '@/features/manage-personnel/ui/PersonnelToolsSheet';
import {useSalaryColumn} from '@/features/manage-salaries/ui/useSalaryColumn';
import {AppButton, IconButton} from '@/shared/ui/button';
import {Page} from '@/shared/ui/page';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';

/**
 * «Сотрудники» — список. Тот же `PeopleList`, что и в личном пространстве (те же
 * данные, те же строки), но с кнопкой «Добавить» и с переходом в УПРАВЛЯЕМУЮ карточку:
 * правка, приглашение, удаление.
 */
type Props = NativeStackScreenProps<PersonnelStackParamList, 'PersonnelList'>;

const styles = StyleSheet.create({grow: {flex: 1}});

export function PersonnelListScreen({navigation}: Props) {
  const {t} = useTranslation();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const {isDesktop} = useBreakpoint();
  const [tools, setTools] = useState(false);

  // Пустой массив без права `salaries/read` — и тогда ни колонки, ни запроса за
  // окладами. Хук зовётся всегда, до раннего возврата ниже.
  const salaryColumn = useSalaryColumn(companyId ?? '');

  const openCard = useCallback(
    (recordId: string, name: string) =>
      navigation.navigate('PersonnelCard', {recordId, name}),
    [navigation],
  );

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.staff" />;
  }

  return (
    <Page
      // Прокрутка одна и принадлежит странице: сетка растёт, а инспектор справа
      // катается сам. На телефоне карточки — тот же список постранично.
      scroll
      gutter={false}
      title={t('nav.staff')}
      testID="staff-screen"
      // Глаголы страницы — в её шапке, и только там: залитый — тот, ради которого
      // экран существует; пакетные операции — обводкой рядом.
      actions={
        <>
          {/* На телефоне второстепенный глагол — значком: две полные кнопки в 390pt
              встают друг под другом. */}
          {isDesktop ? (
            <AppButton
              title={t('detached.title')}
              icon="logout"
              tone="outline"
              size="md"
              onPress={() => navigation.navigate('PersonnelDetached')}
              testID="personnel-detached-open"
            />
          ) : (
            <IconButton
              icon="logout"
              tone="outline"
              size={44}
              label={t('detached.title')}
              onPress={() => navigation.navigate('PersonnelDetached')}
              testID="personnel-detached-open"
            />
          )}
          {isDesktop ? (
            <AppButton
              title={t('bulk.title')}
              icon="bulk"
              tone="outline"
              size="md"
              onPress={() => setTools(true)}
              testID="personnel-tools-open"
            />
          ) : (
            <IconButton
              icon="bulk"
              tone="outline"
              size={44}
              label={t('bulk.title')}
              onPress={() => setTools(true)}
              testID="personnel-tools-open"
            />
          )}
          <AppButton
            title={t('personnel.add')}
            icon="plus"
            size={isDesktop ? 'md' : 'lg'}
            onPress={() => navigation.navigate('PersonnelForm', {})}
            style={isDesktop ? undefined : styles.grow}
            testID="personnel-add"
          />
        </>
      }>
      <PeopleList
        companyId={companyId}
        onOpen={openCard}
        extraColumns={salaryColumn}
        renderDetail={(recordId, name) => (
          <PersonnelCard
            companyId={companyId}
            recordId={recordId}
            personName={name}
            onEdit={() => navigation.navigate('PersonnelForm', {recordId})}
            onDeleted={() => undefined}
          />
        )}
      />
      <PersonnelToolsSheet
        companyId={companyId}
        visible={tools}
        onClose={() => setTools(false)}
      />
    </Page>
  );
}
